import type { AiProviderType, UserSettings, UserPreferences, GeoDecision, VatValidationResult } from '@voice-assistant/shared';
import { authFetch } from './authFetch';

export interface ProfileFeatures {
  commands_enabled: boolean;
  guide_enabled: boolean;
  chunk_worker: boolean;
  memo_cron: boolean;
  memos_enabled: boolean;
  rebuild_enabled: boolean;
  tts_provider: string;
  /** Server-side Whisper STT availability. With tts_provider this derives
   *  `serverVoiceEnabled` — the gate that hides server-voice Settings controls. SE-PF7. */
  whisper_enabled?: boolean;
  github_oauth_enabled: boolean;
  github_pat_enabled: boolean;
  billing_enabled: boolean;
  max_open_tabs?: number;
  free_tier_single_session?: boolean;
  inter_agent_sharing_enabled?: boolean;
  max_consultation_depth?: number;
  contextual_drawer_migration?: boolean;
  ssh_terminal_enabled?: boolean;
  /** Protofac: read Plans/Files from the user's sandbox FS over SSH (SandboxFsService).
   *  Gates the `plansSourceDir` Settings field. Default false (prod/dogfood). SE-PF6 (#1391). */
  sandbox_fs_enabled?: boolean;
  ops_panel_enabled?: boolean;
  /** Dev-preview HMR-from-worktree button (#1403) — admin + prod-only. */
  dev_preview_enabled?: boolean;
  /** Agent Runs — live workflow-progress icon + panel. Admin-only; the authoritative
   *  gate is server-side (workflow_viz_enabled ∧ role==='admin' in WorkflowController),
   *  this flag only decides whether the client bothers to render the affordance. */
  workflow_viz_enabled?: boolean;
  reader_mode_enabled?: boolean;
  logbook_enabled?: boolean;
  /** GearMenu entries — default true; read by App.tsx (were missing from this type). */
  browser_panel_enabled?: boolean;
  pulse_enabled?: boolean;
  /** Session-list feature-surface tabs (SE-PF4) — default true; Protofac sets roads off.
   *  Combined with getBrand().voiceOnlyMode for the developer tabs (Tasks/Plans/Roads). */
  tasks_tab_enabled?: boolean;
  plans_tab_enabled?: boolean;
  roads_tab_enabled?: boolean;
  usage_panel_enabled?: boolean;
  /** In-session model picker (.model-pill-btn + ModelControlSheet) — off on all profiles (#1071). */
  model_selector_enabled?: boolean;
  /** Compact top-bar tuning control (effort + context-window indicator) — on all profiles (#1071). */
  tuning_controls_enabled?: boolean;
  project_chips_enabled?: boolean;
  lesson_generator_enabled?: boolean;
  /** Master Gemini switch. When false (the protofac/valko profiles), the frontend
   *  hides the @gemini mention + Gemini model-picker entries and drops @gemini from
   *  the InputBar placeholder. Default true. */
  gemini_enabled?: boolean;
  /** UI shape (Phase 2.5-F). Reserved profile flag; the frontend currently
   * renders BottomComposer unconditionally and does not read this value. */
  ui_shape?: 'gemini' | 'tradingview' | 'tradingview-team';
}

export async function getProfile(): Promise<{ profile: string; label: string; features: ProfileFeatures }> {
  const res = await authFetch('/api/profile');
  if (!res.ok) throw new Error(`Failed to load profile: ${res.status}`);
  return res.json();
}

const BASE = '/api/settings';

export async function getSettings(): Promise<UserSettings> {
  const res = await authFetch(BASE);
  if (!res.ok) throw new Error(`Failed to load settings: ${res.status}`);
  return res.json();
}

export async function saveApiKey(provider: AiProviderType, apiKey: string): Promise<void> {
  const res = await authFetch(`${BASE}/api-keys/${provider}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apiKey }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Failed to save key: ${res.status}`);
  }
}

export async function deleteApiKey(provider: AiProviderType): Promise<void> {
  const res = await authFetch(`${BASE}/api-keys/${provider}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to delete key: ${res.status}`);
}

// setPreferredProvider retired (model-meta-hardening Phase 6b) — provider is now
// derived per-session from the session model id; there is no per-user provider PUT.

export async function saveUserPreferences(patch: Partial<UserPreferences>): Promise<UserPreferences> {
  const res = await authFetch(`${BASE}/memo`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to save preferences: ${res.status}`);
  const data = await res.json();
  return data.memoSettings;
}

/** @deprecated Use saveUserPreferences */
export const saveMemoSettings = saveUserPreferences;

export async function getProviderStatus(): Promise<{
  activeProvider: string;
  autoMode: boolean;
  anthropicHealthy: boolean;
  routerEnabled: boolean;
  lastError?: string;
  lastErrorAt?: string;
  lastFailoverReason?: string;
}> {
  const res = await authFetch(`${BASE}/provider-status`);
  if (!res.ok) throw new Error(`Failed to load provider status: ${res.status}`);
  return res.json();
}

export async function setAutoFailover(enabled: boolean): Promise<void> {
  const res = await authFetch(`${BASE}/auto-failover`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error(`Failed to set auto-failover: ${res.status}`);
}

export async function setGeminiDeepMode(mode: 'off' | 'detected' | 'all'): Promise<void> {
  const res = await authFetch(`${BASE}/gemini-deep-mode`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
  if (!res.ok) throw new Error(`Failed to set Gemini deep mode: ${res.status}`);
}

export async function setSttCorrectionEnabled(enabled: boolean | null): Promise<void> {
  const res = await authFetch(`${BASE}/stt-correction-enabled`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error(`Failed to set STT correction: ${res.status}`);
}

export async function setSttCorrectionThinking(enabled: boolean | null): Promise<void> {
  const res = await authFetch(`${BASE}/stt-correction-thinking`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error(`Failed to set STT correction thinking: ${res.status}`);
}

export async function setPreferWhisper(value: boolean | null): Promise<void> {
  const res = await authFetch(`${BASE}/prefer-whisper`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw new Error(`Failed to set prefer-whisper: ${res.status}`);
}

export async function setAutoSubmitVoice(value: boolean | null): Promise<void> {
  const res = await authFetch(`${BASE}/auto-submit-voice`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw new Error(`Failed to set auto-submit-voice: ${res.status}`);
}

export async function recordConsent(consents: { tos?: boolean; privacy?: boolean; agentAccess?: boolean; credit?: boolean }): Promise<void> {
  const res = await authFetch(`${BASE}/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(consents),
  });
  if (!res.ok) throw new Error(`Failed to record consent: ${res.status}`);
}

// ── Protofac geo/VAT signup gate ─────────────────────────
/** Mirrors backend GeoService.GeoCheckResult — the server is authoritative. */
export type GeoCheckResult = GeoDecision & { blocked: boolean; vat?: VatValidationResult };

/** Submit the declared signup country (+ Tier-2 VAT ID) to the authoritative server check. */
export async function recordGeo(country: string, vatId?: string): Promise<GeoCheckResult> {
  const res = await authFetch('/api/geo/check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ country, vatId }),
  });
  if (!res.ok) throw new Error(`Failed to check geo: ${res.status}`);
  return res.json();
}

export async function disconnectGithub(): Promise<void> {
  const res = await authFetch('/api/github', { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to disconnect GitHub: ${res.status}`);
}

export async function saveGithubPat(token: string): Promise<{ valid: boolean; reason?: string; login?: string; scopes?: string[]; missingScopes?: string[] }> {
  const res = await authFetch('/api/github/pat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Failed to save PAT: ${res.status}`);
  }
  return res.json();
}

export async function disconnectGithubPat(): Promise<void> {
  const res = await authFetch('/api/github/pat', { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to disconnect GitHub PAT: ${res.status}`);
}

// ── Browser consent ──────────────────────────────────────

export interface BrowserConsentState {
  currentVersion: string;
  acceptedAt: string | null;
  acceptedVersion: string | null;
  upToDate: boolean;
}

export async function getBrowserConsent(): Promise<BrowserConsentState> {
  const res = await authFetch('/api/browser/consent');
  if (!res.ok) throw new Error(`Failed to load browser consent: ${res.status}`);
  return res.json();
}

export async function acceptBrowserConsent(): Promise<BrowserConsentState> {
  const res = await authFetch('/api/browser/consent', { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to accept browser consent: ${res.status}`);
  const data = await res.json();
  return {
    currentVersion: data.currentVersion,
    acceptedAt: data.acceptedAt,
    acceptedVersion: data.acceptedVersion,
    upToDate: data.upToDate,
  };
}

// ── Quota ────────────────────────────────────────────────

export interface QuotaCheckResult {
  allowed: boolean;
  used: number;
  limit: number | null;
  enabled: boolean;
}

export async function checkQuota(feature: string): Promise<QuotaCheckResult> {
  const res = await authFetch(`/api/quota/check?feature=${encodeURIComponent(feature)}`);
  if (!res.ok) return { allowed: true, used: 0, limit: null, enabled: true }; // fail open
  return res.json();
}

export async function incrementQuota(feature: string): Promise<void> {
  await authFetch(`/api/quota/increment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feature }),
  }).catch(() => {}); // fire-and-forget
}
